import { format, parse, isValid, formatDistance, differenceInMinutes, addDays, startOfDay, endOfDay } from 'date-fns'
import { toZonedTime, fromZonedTime } from 'date-fns-tz'
import { ptBR } from 'date-fns/locale'

/**
 * Gerenciador centralizado de datas para o projeto Plattus
 *
 * OBJETIVOS:
 * - Unificar o tratamento de datas em todo o projeto
 * - Garantir consistência de timezone (Brasil/São Paulo)
 * - Eliminar uso de moment.js, dayjs e parsing manual
 * - Melhorar performance e segurança
 */

const BR_TZ = 'America/Sao_Paulo'
const DEFAULT_FORMAT = 'dd/MM/yyyy HH:mm'
const DATE_ONLY_FORMAT = 'dd/MM/yyyy'
const TIME_ONLY_FORMAT = 'HH:mm'
const ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSSxxx"

export class DateManager {
  /**
   * Formata uma data para o fuso horário brasileiro
   *
   * @param date - Data em qualquer formato (string ISO, Date, timestamp)
   * @param formatStr - Formato de saída (padrão: dd/MM/yyyy HH:mm)
   * @returns Data formatada em português brasileiro
   */
  static formatToBR(date: string | Date | number, formatStr = DEFAULT_FORMAT): string {
    try {
      const dateObj = this.ensureDate(date)
      if (!dateObj) {
        console.warn('DateManager.formatToBR: ensureDate returned null', { date, formatStr })
        return ''
      }

      const zonedDate = toZonedTime(dateObj, BR_TZ)
      const result = format(zonedDate, formatStr, {
        locale: ptBR,
      })

      return result
    } catch (error) {
      console.warn('DateManager.formatToBR: Invalid date', { date, formatStr, error })
      return ''
    }
  }

  /**
   * Converte string brasileira para Date object seguro
   *
   * @param dateStr - String no formato brasileiro (dd/MM/yyyy ou dd/MM/yyyy HH:mm)
   * @param format - Formato de entrada (padrão: dd/MM/yyyy HH:mm)
   * @returns Date object ou null se inválido
   */
  static parseBRDate(dateStr: string, format = DEFAULT_FORMAT): Date | null {
    try {
      if (!dateStr || typeof dateStr !== 'string') return null

      const parsed = parse(dateStr.trim(), format, new Date())
      return isValid(parsed) ? fromZonedTime(parsed, BR_TZ) : null
    } catch (error) {
      console.warn('DateManager.parseBRDate: Parse failed', { dateStr, format, error })
      return null
    }
  }

  /**
   * Calcula diferença entre duas datas em minutos (timezone-safe)
   *
   * @param date1 - Data inicial
   * @param date2 - Data final (padrão: agora)
   * @returns Diferença em minutos
   */
  static diffMinutes(date1: string | Date, date2: string | Date = new Date()): number {
    try {
      const d1 = this.ensureDate(date1)
      const d2 = this.ensureDate(date2)

      if (!d1 || !d2) return 0

      const zoned1 = toZonedTime(d1, BR_TZ)
      const zoned2 = toZonedTime(d2, BR_TZ)

      return differenceInMinutes(zoned2, zoned1)
    } catch (error) {
      console.warn('DateManager.diffMinutes: Calculation failed', { date1, date2, error })
      return 0
    }
  }

  /**
   * Formata tempo relativo (ex: "há 2 horas", "em 5 minutos")
   *
   * @param date - Data para comparar
   * @param baseDate - Data de referência (padrão: agora)
   * @returns String formatada em português
   */
  static timeAgo(date: string | Date, baseDate: Date = new Date()): string {
    try {
      const dateObj = this.ensureDate(date)
      if (!dateObj) return ''

      const zonedDate = toZonedTime(dateObj, BR_TZ)
      const zonedBase = toZonedTime(baseDate, BR_TZ)

      return formatDistance(zonedDate, zonedBase, {
        addSuffix: true,
        locale: ptBR,
      })
    } catch (error) {
      console.warn('DateManager.timeAgo: Formatting failed', { date, baseDate, error })
      return ''
    }
  }

  /**
   * Converte para horário apenas (HH:mm)
   *
   * @param date - Data qualquer
   * @returns Horário brasileiro (HH:mm)
   */
  static getTimeBR(date: string | Date): string {
    return this.formatToBR(date, TIME_ONLY_FORMAT)
  }

  /**
   * Converte para data apenas (dd/MM/yyyy)
   *
   * @param date - Data qualquer
   * @returns Data brasileira (dd/MM/yyyy)
   */
  static getDateBR(date: string | Date): string {
    return this.formatToBR(date, DATE_ONLY_FORMAT)
  }

  /**
   * Converte para ISO string no timezone brasileiro
   *
   * @param date - Data qualquer
   * @returns ISO string ajustado para BR timezone
   */
  static toISOBR(date: string | Date): string {
    try {
      const dateObj = this.ensureDate(date)
      if (!dateObj) {
        console.warn('DateManager.toISOBR: Invalid date, using current date as fallback', { date })
        const now = new Date()
        const zonedNow = toZonedTime(now, BR_TZ)
        return format(zonedNow, ISO_FORMAT)
      }

      const zonedDate = toZonedTime(dateObj, BR_TZ)
      const result = format(zonedDate, ISO_FORMAT)

      if (!result || result.length === 0) {
        console.warn('DateManager.toISOBR: Empty result, using current date as fallback', { date })
        const now = new Date()
        const zonedNow = toZonedTime(now, BR_TZ)
        return format(zonedNow, ISO_FORMAT)
      }

      return result
    } catch (error) {
      console.warn('DateManager.toISOBR: Conversion failed, using current date as fallback', { date, error })
      const now = new Date()
      const zonedNow = toZonedTime(now, BR_TZ)
      return format(zonedNow, ISO_FORMAT)
    }
  }

  /**
   * Valida se uma data está dentro do limite de 24h (regra WhatsApp)
   *
   * @param lastMessageDate - Data da última mensagem
   * @param limitHours - Limite em horas (padrão: 23.83 = 23h50min)
   * @returns true se ainda pode enviar mensagem
   */
  static canSendMetaMessage(lastMessageDate: string | Date, limitHours = 23.83): boolean {
    try {
      const diffMinutes = this.diffMinutes(lastMessageDate)
      return diffMinutes < limitHours * 60
    } catch {
      return false
    }
  }

  /**
   * Cria range de datas para consultas (início e fim do dia)
   *
   * @param startDate - Data inicial
   * @param endDate - Data final (opcional)
   * @returns Objeto com start e end em formato ISO
   */
  static createDateRange(startDate: string | Date, endDate?: string | Date) {
    try {
      const start = this.ensureDate(startDate)
      const end = endDate ? this.ensureDate(endDate) : start

      if (!start || !end) return null

      const startZoned = toZonedTime(startOfDay(start), BR_TZ)
      const endZoned = toZonedTime(endOfDay(end), BR_TZ)

      return {
        start: this.toISOBR(startZoned),
        end: this.toISOBR(endZoned),
      }
    } catch (error) {
      console.warn('DateManager.createDateRange: Range creation failed', { startDate, endDate, error })
      return null
    }
  }

  /**
   * Adiciona dias a uma data (timezone-safe)
   *
   * @param date - Data base
   * @param days - Número de dias (pode ser negativo)
   * @returns Nova data
   */
  static addDaysBR(date: string | Date, days: number): Date | null {
    try {
      const dateObj = this.ensureDate(date)
      if (!dateObj) return null

      const zonedDate = toZonedTime(dateObj, BR_TZ)
      const newDate = addDays(zonedDate, days)

      return fromZonedTime(newDate, BR_TZ)
    } catch (error) {
      console.warn('DateManager.addDaysBR: Addition failed', { date, days, error })
      return null
    }
  }

  /**
   * Obtém o ano atual no fuso horário brasileiro
   *
   * @returns Ano atual como número
   */
  static getCurrentYear(): number {
    try {
      const now = new Date()
      const zonedDate = toZonedTime(now, BR_TZ)
      return zonedDate.getFullYear()
    } catch (error) {
      console.warn('DateManager.getCurrentYear: Failed to get current year', { error })
      return new Date().getFullYear() // fallback
    }
  }

  /**
   * Formata uma string de período substituindo datas no formato YYYY-MM-DD
   * por uma representação localizada (padrão: dd/MM/yyyy).
   *
   * Exemplos:
   *  - "2025-08-01 - 2025-08-31" => "01/08/2025 - 31/08/2025"
   *  - "Período: 2025-12-01" => "Período: 01/12/2025"
   *
   * @param period - String que pode conter datas no formato YYYY-MM-DD
   * @param formatStr - Formato de saída para cada data encontrada (padrão: dd/MM/yyyy)
   * @returns string com as datas formatadas quando possível
   */
  static formatPeriodString(period: string | null | undefined, formatStr = DATE_ONLY_FORMAT): string {
    if (!period || typeof period !== 'string') return ''

    try {
      // Encontra ocorrências de datas no formato YYYY-MM-DD
      return period.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (match) => {
        const dateObj = this.ensureDate(match)
        if (!dateObj) return match
        // Reutiliza o formatToBR que respeita timezone e locale
        return this.formatToBR(dateObj, formatStr)
      })
    } catch (error) {
      console.warn('DateManager.formatPeriodString: Formatting failed', { period, error })
      return period
    }
  }

  /**
   * Utilitário interno: garante que entrada seja Date object
   */
  private static ensureDate(date: string | Date | number): Date | null {
    if (date instanceof Date) {
      return isValid(date) ? date : null
    }

    if (typeof date === 'number') {
      const dateObj = new Date(date)
      return isValid(dateObj) ? dateObj : null
    }

    if (typeof date === 'string') {
      // Tenta parse ISO primeiro
      const isoDate = new Date(date)
      if (isValid(isoDate)) return isoDate

      // Tenta parse brasileiro
      return this.parseBRDate(date) || this.parseBRDate(date, DATE_ONLY_FORMAT)
    }

    return null
  }

  /**
   * Utilitários para casos específicos do projeto
   */
  static readonly utils = {
    /**
     * Formata data de nascimento (sempre dd/MM/yyyy)
     */
    formatBirthday: (date: string): string => {
      if (!date) return ''

      // Se vier no formato YYYY-MM-DD (do backend)
      if (date.includes('-') && date.length === 10) {
        const [year, month, day] = date.split('-')
        return `${day}/${month}/${year}`
      }

      return DateManager.getDateBR(date)
    },

    /**
     * Parse seguro de data de nascimento
     */
    parseBirthday: (dateStr: string): Date | null => {
      return DateManager.parseBRDate(dateStr, DATE_ONLY_FORMAT)
    },

    /**
     * Formata para URLs de API (YYYY-MM-DD)
     */
    formatForAPI: (date: string | Date): string => {
      try {
        const dateObj = DateManager.ensureDate(date)
        if (!dateObj) {
          console.warn('DateManager.utils.formatForAPI: Invalid date, using current date as fallback', { date })
          const now = new Date()
          const zonedNow = toZonedTime(now, BR_TZ)
          return format(zonedNow, 'yyyy-MM-dd')
        }

        // Usar toZonedTime para timezone brasileiro e formatar apenas como YYYY-MM-DD
        const zonedDate = toZonedTime(dateObj, BR_TZ)
        const formatted = format(zonedDate, 'yyyy-MM-dd')

        if (!formatted || formatted.length === 0) {
          console.warn('DateManager.utils.formatForAPI: Empty result, using current date as fallback', { date })
          const now = new Date()
          const zonedNow = toZonedTime(now, BR_TZ)
          return format(zonedNow, 'yyyy-MM-dd')
        }

        return formatted
      } catch (error) {
        console.warn('DateManager.utils.formatForAPI: Formatting failed, using current date as fallback', {
          date,
          error,
        })
        const now = new Date()
        const zonedNow = toZonedTime(now, BR_TZ)
        return format(zonedNow, 'yyyy-MM-dd')
      }
    },

    /**
     * Formata datetime brasileiro para API (UTC ISO string)
     * Converte um datetime local brasileiro para UTC para envio ao backend
     *
     * @param date - Data (Date object ou string)
     * @param time - Hora (Date object do VaTimeInput ou string HH:mm)
     * @returns ISO string em UTC para o backend
     */
    formatDateTimeForAPI: (date: string | Date, time?: string | Date): string => {
      try {
        const dateObj = DateManager.ensureDate(date)
        if (!dateObj) {
          console.warn('DateManager.utils.formatDateTimeForAPI: Invalid date, using current date as fallback', {
            date,
            time,
          })
          return new Date().toISOString()
        }

        // Criar novo Date object com a data fornecida
        const dateTime = new Date(dateObj)

        // Se hora foi fornecida, aplicar ela
        if (time) {
          if (time instanceof Date) {
            // VaTimeInput retorna Date - extrair apenas hora e minuto
            dateTime.setHours(time.getHours(), time.getMinutes(), 0, 0)
          } else if (typeof time === 'string') {
            // String no formato "HH:mm"
            const [hours, minutes] = time.split(':').map(Number)
            dateTime.setHours(hours || 0, minutes || 0, 0, 0)
          }
        }

        // Tratar o dateTime como horário brasileiro e converter para UTC
        // fromZonedTime faz a conversão: horário brasileiro -> UTC
        const utcDateTime = fromZonedTime(dateTime, BR_TZ)

        return utcDateTime.toISOString()
      } catch (error) {
        console.warn('DateManager.utils.formatDateTimeForAPI: Formatting failed, using current date as fallback', {
          date,
          time,
          error,
        })
        return new Date().toISOString()
      }
    },

    /**
     * Verifica se é hoje
     */
    isToday: (date: string | Date): boolean => {
      const dateStr = DateManager.getDateBR(date)
      const todayStr = DateManager.getDateBR(new Date())
      return dateStr === todayStr
    },
  }
}

/**
 * EXEMPLOS DE USO:
 *
 * // Formatação
 * DateManager.formatToBR('2025-08-15T12:00:00Z') // "15/08/2025 09:00"
 * DateManager.getTimeBR('2025-08-15T12:00:00Z')  // "09:00"
 * DateManager.getDateBR('2025-08-15T12:00:00Z')  // "15/08/2025"
 *
 * // Parse seguro
 * DateManager.parseBRDate('15/08/2025 14:30')    // Date object
 * DateManager.parseBRDate('invalid')             // null
 *
 * // Cálculos
 * DateManager.diffMinutes('2025-08-15T12:00:00Z') // minutos até agora
 * DateManager.timeAgo('2025-08-15T10:00:00Z')     // "há 2 horas"
 *
 * // WhatsApp
 * DateManager.canSendMetaMessage(lastMessage) // true/false
 *
 * // Utilitários
 * DateManager.utils.formatBirthday('1990-12-25')  // "25/12/1990"
 * DateManager.utils.formatForAPI('15/08/2025')    // "2025-08-15"
 *
 * // DateTime para API (IMPORTANTE: converte BR para UTC)
 * const date = new Date('2025-08-15') // Data local
 * const time = new Date() // VaTimeInput com 14:30
 * time.setHours(14, 30, 0, 0)
 * DateManager.utils.formatDateTimeForAPI(date, time) // "2025-08-15T17:30:00.000Z" (UTC +3h)
 */

export default DateManager
